import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  if (!token) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <AlertCircle size={40} className="text-eduhu-error mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-eduhu-dark">Invalid link</h1>
          <p className="mt-3 text-eduhu-inkSoft">This password reset link is missing a token. Please request a new one.</p>
          <Link to="/forgot-password" className="mt-6 inline-block eduhu-btn-primary">
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setErr("Passwords do not match.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      setDone(true);
    } catch (er) {
      setErr(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2 border-b border-eduhu-border">
      <div className="hidden lg:flex bg-eduhu-dark text-eduhu-bg grain p-12 flex-col justify-between">
        <div className="font-serif text-6xl text-eduhu-gold leading-none">
          New<br />password.
        </div>
        <div className="text-sm text-eduhu-bg/60 max-w-xs">
          Choose a strong password — at least 12 characters, with an uppercase letter,
          lowercase letter, number, and symbol.
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        {done ? (
          <div className="w-full max-w-md text-center">
            <div className="flex justify-center mb-6">
              <CheckCircle2 size={48} className="text-eduhu-success" />
            </div>
            <h1 className="font-serif text-4xl text-eduhu-dark">Password updated</h1>
            <p className="mt-4 text-eduhu-inkSoft">
              Your password has been changed. You can now sign in with your new password.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="mt-8 eduhu-btn-primary"
            >
              Sign in
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md">
            <div className="flex justify-center mb-8">
              <Link to="/" aria-label="EDUHUB home">
                <img src="/logo.png" alt="EDUHUB logo" className="w-16 h-16 object-contain" />
              </Link>
            </div>

            <div className="overline text-eduhu-claret mb-3">Password reset</div>
            <h1 className="font-serif text-4xl lg:text-5xl text-eduhu-dark tracking-tight">
              Set new password
            </h1>

            <label className="block mt-10">
              <div className="overline text-eduhu-inkSoft mb-2">New password</div>
              <input
                type="password"
                required
                minLength={12}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-eduhu-bg2 border border-eduhu-border px-4 py-3 focus:outline-none focus:border-eduhu-dark"
              />
            </label>

            <label className="block mt-6">
              <div className="overline text-eduhu-inkSoft mb-2">Confirm new password</div>
              <input
                type="password"
                required
                minLength={12}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-eduhu-bg2 border border-eduhu-border px-4 py-3 focus:outline-none focus:border-eduhu-dark"
              />
            </label>

            {err && <div className="mt-4 text-sm text-eduhu-error">{err}</div>}

            <button disabled={busy} className="eduhu-btn-primary w-full justify-center mt-8">
              {busy ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
